import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FileText, FlaskConical } from "lucide-react";
import { motion } from "framer-motion";
import "./test-details.css";
import Loading from "../loading/Loading";
import { GlobalContext } from "../../GlobalContext";

const formatPrice = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return "₦0.00";
  }
  let numAmount;
  if (typeof amount === 'string') {
    const cleanAmount = amount.replace(/[₦,]/g, '').replace(/\.00$/, '');
    numAmount = parseFloat(cleanAmount);
  } else {
    numAmount = parseFloat(amount);
  }
  if (isNaN(numAmount)) {
    return "₦0.00";
  }
  return `₦${numAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const TestDetails = () => {
  const navigate = useNavigate();
  const { test_slug } = useParams(); // Extract test_slug from URL
  const { userData } = useContext(GlobalContext); // Access userData from GlobalContext
  const [testData, setTestData] = useState({
    name: "N/A",
    price: "N/A",
    processingTime: "N/A",
    sampleType: "N/A",
    content: "N/A",
    orderLink: "#",
    req_form_link: "",
  });
  const [isLoading, setIsLoading] = useState(true);

  const API_ENDPOINT = `/wp-json/dbsb/v1/medical-test?test_slug=${test_slug}`;

  // Fetch test details using the slug
  useEffect(() => {
    const fetchTestDetails = async () => {
      setIsLoading(true);
      try {
        if (!window.wpApiSettings?.nonce) {
          console.error("Authentication token not found.");
          setTestData({
            name: "N/A",
            price: "N/A",
            processingTime: "N/A",
            sampleType: "N/A",
            content: "N/A",
            orderLink: "#",
            req_form_link: "",
          });
          setIsLoading(false);
          return;
        }

        const response = await fetch(API_ENDPOINT, {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-WP-Nonce": window.wpApiSettings.nonce,
          },
        });

        if (!response.ok) {
          console.error(`Failed to fetch test details: ${response.status}`);
          setTestData({
            name: "N/A",
            price: "N/A",
            processingTime: "N/A",
            sampleType: "N/A",
            content: "N/A",
            orderLink: "#",
            req_form_link: "",
          });
          setIsLoading(false);
          return;
        }

        const data = await response.json();
        // Map API response to required fields, fallback to "N/A" if field is missing
        setTestData({
          name: data.title || "N/A",
          slug: data.slug,
          price: data.test_price ? data.test_price.toString() : "N/A",
          processingTime: data.processing_time || "N/A",
          sampleType: data.sample_type || "N/A",
          content: data.content || "N/A",
          orderLink: data.order_link || "#",
          req_form_link: data.req_form_link || "",
        });
      } catch (error) {
        console.error("Error fetching test details:", error);
        setTestData({
          name: "N/A",
          price: "N/A",
          processingTime: "N/A",
          sampleType: "N/A",
          content: "N/A",
          orderLink: "#",
          req_form_link: "",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchTestDetails();
  }, [test_slug]);

  // Animation variants for smooth transitions
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.5,
        when: "beforeChildren",
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 20,
      },
    },
  };

  const buttonVariants = {
    hover: { scale: 1.05, transition: { duration: 0.2 } },
    tap: { scale: 0.95, transition: { duration: 0.1 } },
  };

  return (
    <motion.div
      className="test-detail-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {isLoading ? (
        <Loading />
      ) : (
        <>
          <motion.div
            className="test-detail-header-container"
            variants={itemVariants}
          >
            <h1>{testData.name}</h1>
            {userData.role === "provider" && (
              <motion.a
                href={testData.req_form_link}
                className="download-button"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
                download
              >
                Download requisition form
              </motion.a>
            )}
          </motion.div>

          <motion.div className="test-detail-card" variants={itemVariants}>
            {/* Left Side */}
            <div className="test-detail-left">
              <h2>Test details</h2>
              <div className="test-details-box">
                <h3>{testData.name}</h3>
                <div className="detail-item">
                  <span className="detail-label">Price</span>
                  <span className="detail-value price-detail-value">
                    {isNaN(Number(testData.price)) ? testData.price : formatPrice(testData.price)}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">
                    Estimated processing time
                  </span>
                  <span className="detail-value">
                    {testData.processingTime}
                  </span>
                </div>
                <div className="detail-item">
                  <span className="detail-label">Sample type required</span>
                  <span className="detail-value">{testData.sampleType}</span>
                </div>
              </div>
              <div className="divider"></div>
              <div className="button-group">
                <motion.button
                  onClick={() => navigate(`/review-test-order?test_slug=${testData.slug}`)}
                  className="place-order-button"
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  Place Order
                </motion.button>
                <motion.button
                  onClick={() => navigate("/talk-to-specialist")}
                  className="talk-to-specialist-button"
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  Talk to a specialist
                </motion.button>
              </div>
            </div>

            {/* Right Side */}
            <div className="test-detail-right">
              <div className="subheader">What is {testData.name}?</div>
              <div
                className="test-excerpt"
                dangerouslySetInnerHTML={{
                  __html: (testData.content || "")
                    .replace(/\n/g, "")
                    .replace(/<p>/g, '<p style="margin-bottom: 0.8rem;">')
                }}
              />
            </div>
          </motion.div>
        </>
      )}
    </motion.div>
  );
};

export default TestDetails;